<template>
    <div class="d-flex justify-space-between">
        <div class="document-selector-title d-flex align-center">
            <v-icon class="paperclip-icon" color="neutral-80">mdi-paperclip</v-icon>
            <p>Documentos</p>
        </div>
        <v-btn :disabled="props.modelValue.length >= props.maxArchives" color="#5C5F5F" class="px-6 py-5 d-flex upload-button"
            @click="fileInput.click()" append-icon="mdi-cloud-upload" variant="text" rounded="xs">
            anexar
        </v-btn>
    </div>
    <div class="uploads-chip-container w-100 d-flex ga-1 justify-start flex-wrap">
        <v-chip v-for="(archive, index) in props.modelValue" :key="index" variant="outlined" color="neutral-35"
            class="archive-chips d-flex ga-2 py-1 px-2">
            <div>
                <rb-loader v-model="archive.loader" :contained="true" :generic="true" size="22" />
            </div>
            <div @click="downloadArchive(archive)" class="chip-text-content"
                @mouseenter="checkOverflow($event, archive.name)">
                {{ archive.name }}
            </div>
            <v-icon icon="mdi-close" @click="removeArchive(index, archive.name)"></v-icon>
            <v-tooltip v-model="overflows[archive.name]" :disabled="!overflows[archive.name]" activator="parent"
                location="top">
                {{ archive.name }}
            </v-tooltip>
        </v-chip>
        <input :accept="validFilesTypes"
            style="display: none" ref="fileInput" @change="addArchive" type="file" multiple>
    </div>
    <p class="limit-upload-text text-left px-4">Limite de {{maxSizeMegaBytes}}MB.</p>
</template>

<script setup>
import { defineProps, defineEmits, ref, reactive } from 'vue';
import { useSnackbarStore } from '@/ai_studio/stores/snackbar';
const snackbar = useSnackbarStore();
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';
const validFilesTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv'];
const emit = defineEmits(['update:modelValue']);
const props = defineProps({
    modelValue: { type: Array, default: () => [] },
    maxSizeMegaBytes: { type: Number, default: 4 },
    maxArchives: { type: Number, default: 10 }
});

const fileInput = ref(null);
const overflows = reactive({});
const checkOverflow = (event, archivename) => {
    const element = event.target;
    if (element.scrollWidth > element.clientWidth) overflows[archivename] = true;
};

const addArchive = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(validateFile);
    
    if ((props.modelValue.length + validFiles.length) > props.maxArchives){
        snackbar.error(`Máximo upload permitido de ${props.maxArchives} arquivos.`);
        return;
    }

    if (validFiles.length) {
        emit('update:modelValue', [...props.modelValue, ...validFiles]);
    }
    
    fileInput.value.value = '';
};

const validateFile = (file) => {
    const validFormat = validFilesTypes.includes(file.type)
    const maxSizeBytes = props.maxSizeMegaBytes * 1024 * 1024;
    const isDuplicate = props.modelValue.some(archive => archive.name === file.name);

    if (!validFormat) {
        snackbar.error(`Arquivo "${file.name}" possui formato não suportado.`);
        return false;
    }
    
    if (file.size > maxSizeBytes) {
        snackbar.error(`Arquivo "${file.name}" excede o tamanho máximo de ${props.maxSizeMegaBytes}MB.`);
        return false;
    }
    
    if (isDuplicate) {
        return false;
    }
    
    return true;
};

const removeArchive = (index, archivename) => {
    const archives = [...props.modelValue];
    const archive = archives[index];

    if (!archive || archive.name !== archivename) return;

    archives.splice(index, 1);
    emit('update:modelValue', archives);
    delete overflows[archivename];
};

const downloadArchive = async (archive) => {
    try {
        let blob;

        if (archive.reference) {
            archive.loader = true;
            const response = await fetch(`/api/assistents/uploads/${archive.url}`);
            blob = await response.blob();
            archive.loader = false;
            snackbar.success('Arquivo baixado com sucesso.');
            createAndTriggerDownload(blob, archive.name);
            return;
        }

        blob = archive;
        createAndTriggerDownload(blob, archive.name);
        snackbar.success('Arquivo baixado com sucesso.');
    } catch (error) {
        console.error(error);
        snackbar.error('Erro ao baixar o arquivo');
    }
};

const createAndTriggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};
</script>

<style scoped>
.upload-button {
    font-size: 12px;
    font-weight: 400;
    padding-block: 4px;
}

.upload-button:deep(.v-btn__append) {
    color: rgb(var(--v-theme-neutral-80));
    font-size: 18px;
}

.archive-chips {
    max-width: 160px;
    position: relative;
    border-radius: 8px;
    border: 1px solid rgb(var(--v-theme-neutral-80));
}

.archive-chips:deep(.v-chip__content) {
    max-width: 100%;
    font-size: 14px !important;
    font-weight: 500;
    letter-spacing: 0;
    line-height: 20px;
}

.archive-chips:deep(.v-chip__content i) {
    margin-left: 8px;
}

.archive-chips:deep(.chip-text-content) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.archive-chips:deep(.chip-text-content):hover {
    text-decoration: underline;
}

.limit-upload-text {
    color: rgb(var(--v-theme-neutral-35));    
    font-size: 12px;
    line-height: 16px;
}

.document-selector-title p {
    font-size: 12px;
    color: #5C5F5F;
    line-height: 16px;
}

.paperclip-icon {
    transform: rotate(35deg);
    margin-right: 4px;
}
</style>
